import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import clsx from 'clsx'
import {
  ChartScatterDataSchema,
  ChartScatterUISchema,
  DataPoint,
  SeriesData,
  TooltipConfig,
  isSingleSeriesData,
} from './types'
import { ChartConfig, ChartContainer, ChartTooltip } from '@/components/shadcn/charts'
import { CartesianGrid, Customized, Scatter, ScatterChart, XAxis, YAxis } from 'recharts'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { normalizeRechartsData } from '../shared/rechartsDataUtils'
import { getComponentHeights } from '../utils'

/** Stable numeric plot keys — Recharts Scatter needs number axes for categorical grids. */
const PLOT_X = 'x'
const PLOT_Y = 'y'
const PLOT_Z = 'z'

interface ScatterTooltipProps {
  active?: boolean
  payload?: Array<{
    payload: Record<string, unknown>
    name?: string
    unit?: string
  }>
  xAxis: { name?: string; unit?: string }
  yAxis: { name?: string; unit?: string }
  zAxis: { name?: string; unit?: string }
  xDataKey: string
  yDataKey: string
  zDataKey: string
  tooltip?: TooltipConfig
}

function ScatterTooltipContent({
  active,
  payload,
  xAxis,
  yAxis,
  zAxis,
  xDataKey,
  yDataKey,
  zDataKey,
  tooltip,
}: ScatterTooltipProps) {
  if (!active || !payload?.length) {
    return null
  }

  const data = payload[0].payload
  const { showX = true, showY = true, showZ = true, fields } = tooltip ?? {}

  return (
    <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-offwhite px-2.5 py-1.5 text-xs shadow-xl">
      <div className="grid gap-1">
        {showX && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{xAxis.name || 'X'}</span>
            <span className="font-mono font-medium tabular-nums text-foreground">
              {String(data[xDataKey])}
              {xAxis.unit && <span className="ml-0.5 text-muted-foreground">{xAxis.unit}</span>}
            </span>
          </div>
        )}
        {showY && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{yAxis.name || 'Y'}</span>
            <span className="font-mono font-medium tabular-nums text-foreground">
              {String(data[yDataKey])}
              {yAxis.unit && <span className="ml-0.5 text-muted-foreground">{yAxis.unit}</span>}
            </span>
          </div>
        )}
        {showZ && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{zAxis.name || 'Z'}</span>
            <span className="font-mono font-medium tabular-nums text-foreground">
              {String(data[zDataKey])}
              {zAxis.unit && <span className="ml-0.5 text-muted-foreground">{zAxis.unit}</span>}
            </span>
          </div>
        )}
        {fields?.map((field) => {
          const value = data[field.dataKey]
          if (value === undefined) return null
          return (
            <div key={field.dataKey} className="flex justify-between gap-4">
              <span className="text-muted-foreground">{field.label}</span>
              <span className="font-mono font-medium tabular-nums text-foreground">
                {String(value)}
                {field.unit && <span className="ml-0.5 text-muted-foreground">{field.unit}</span>}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function toGradientId(key: string) {
  return `bubble-${String(key).replace(/[^a-zA-Z0-9_-]/g, '')}`
}

function getTooltipTargetRadius(zRange: number[]) {
  const maxArea = Math.max(...zRange.filter(Number.isFinite), 16)
  return Math.sqrt(maxArea / Math.PI)
}

function ScatterTooltipTarget({ radius, cx, cy }: { radius: number; cx?: number; cy?: number }) {
  return <circle cx={cx} cy={cy} r={radius} fill="transparent" stroke="none" pointerEvents="all" />
}

function pickSizeLegendValues(values: number[]): number[] {
  if (values.length === 0) return []
  const min = Math.min(...values)
  const max = Math.max(...values)
  if (min === max) return [min]
  const steps = 4
  return Array.from({ length: steps }, (_, index) => Math.round(min + ((max - min) * index) / (steps - 1)))
}

function SizeLegend({ title, values, zRange }: { title?: string; values: number[]; zRange: number[] }) {
  if (values.length === 0) return null

  const [rangeMin = 40, rangeMax = 400] = zRange
  const dataMin = Math.min(...values)
  const dataMax = Math.max(...values)
  const span = dataMax - dataMin || 1

  return (
    <div className="flex flex-col items-start gap-2 text-xs text-muted-foreground">
      {title && <span className="font-medium text-foreground">{title}</span>}
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
        {values.map((value) => {
          const t = (value - dataMin) / span
          const diameter = Math.sqrt(rangeMin + t * (rangeMax - rangeMin)) / 2
          const size = Math.max(8, Math.min(28, diameter))
          return (
            <div key={value} className="flex flex-col items-center gap-1">
              <div
                className="rounded-full border border-muted-foreground/60 bg-muted-foreground/30"
                style={{ width: size, height: size }}
              />
              <span className="tabular-nums">{value}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ColourLegend({ config }: { config: ChartConfig }) {
  return (
    <div className="mt-3 flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
      {Object.entries(config).map(([key, item]) => (
        <div key={key} className="flex max-w-full shrink-0 items-center gap-1.5 whitespace-nowrap">
          <div className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
          <span className="min-w-0 max-w-full truncate">{typeof item.label === 'string' ? item.label : key}</span>
        </div>
      ))}
    </div>
  )
}

function resolveCategoryLabels(
  points: Array<Record<string, unknown>>,
  dataKey: string,
  domain: Array<string | number> | undefined
): string[] {
  if (domain?.length) {
    return domain.map(String)
  }
  const labels: string[] = []
  const seen = new Set<string>()
  for (const point of points) {
    const value = point[dataKey]
    if (value === undefined || value === null) continue
    const label = String(value)
    if (seen.has(label)) continue
    seen.add(label)
    labels.push(label)
  }
  return labels
}

function buildSingleSeriesPlotPoints({
  points,
  xDataKey,
  yDataKey,
  zDataKey,
  colorKey,
  xType,
  yType,
  xDomain,
  yDomain,
}: {
  points: Array<Record<string, unknown>>
  xDataKey: string
  yDataKey: string
  zDataKey: string
  colorKey: string
  xType: string
  yType: string
  xDomain?: Array<string | number>
  yDomain?: Array<string | number>
}): {
  plotPoints: Array<Record<string, unknown>>
  xCategoryLabels?: string[]
  yCategoryLabels?: string[]
} {
  const xIsCategory = xType === 'category'
  const yIsCategory = yType === 'category'
  const xCategoryLabels = xIsCategory ? resolveCategoryLabels(points, xDataKey, xDomain) : undefined
  const yCategoryLabels = yIsCategory ? resolveCategoryLabels(points, yDataKey, yDomain) : undefined
  const xIndex = new Map((xCategoryLabels ?? []).map((label, index) => [label, index]))
  const yIndex = new Map((yCategoryLabels ?? []).map((label, index) => [label, index]))

  const plotPoints = points.map((point) => {
    const xValue = xIsCategory ? (xIndex.get(String(point[xDataKey])) ?? 0) : Number(point[xDataKey])
    const yValue = yIsCategory ? (yIndex.get(String(point[yDataKey])) ?? 0) : Number(point[yDataKey])
    const zValue = Number(point[zDataKey])

    return {
      ...point,
      [PLOT_X]: xValue,
      [PLOT_Y]: yValue,
      [PLOT_Z]: Number.isFinite(zValue) ? zValue : 0,
      __color: String(point[colorKey] ?? ''),
    }
  })

  return { plotPoints, xCategoryLabels, yCategoryLabels }
}

function BubbleGradients({ keys, config }: { keys: string[]; config: ChartConfig }) {
  return (
    <defs>
      {keys.map((key) => {
        const color = config[key]?.color
        if (!color) return null
        const gradientId = toGradientId(key)
        return (
          <radialGradient key={gradientId} id={gradientId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={color} stopOpacity={0} />
            <stop offset="60%" stopColor={color} stopOpacity={0.2} />
            <stop offset="85%" stopColor={color} stopOpacity={0.6} />
            <stop offset="100%" stopColor={color} stopOpacity={1} />
          </radialGradient>
        )
      })}
    </defs>
  )
}

interface AxisScale {
  scale: (value: number | string) => number | undefined
  bandwidth?: number
  bandSize?: number
}

interface BubbleGridRendererProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  xAxisMap?: Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisMap?: Record<string, any>
  plotPoints: Array<Record<string, unknown>>
  config: ChartConfig
  dotStyle: 'bubble' | 'solid'
  zRange: number[]
}

/**
 * Draw bubbles via axis scales (same approach as ChartDumbbell).
 * ScatterChart positioning is unreliable for categorical grids; ComposedChart + Customized is not.
 */
function BubbleGridRenderer({ xAxisMap, yAxisMap, plotPoints, config, dotStyle, zRange }: BubbleGridRendererProps) {
  if (!xAxisMap || !yAxisMap) return null

  const xAxis = Object.values(xAxisMap)[0] as AxisScale | undefined
  const yAxis = Object.values(yAxisMap)[0] as AxisScale | undefined
  if (!xAxis?.scale || !yAxis?.scale) return null

  const zValues = plotPoints.map((point) => Number(point[PLOT_Z])).filter((value) => Number.isFinite(value))
  const dataMin = zValues.length ? Math.min(...zValues) : 0
  const dataMax = zValues.length ? Math.max(...zValues) : 1
  const span = dataMax - dataMin || 1
  const [rangeMin = 40, rangeMax = 400] = zRange

  return (
    <g className="scatter-bubble-grid" pointerEvents="none">
      {plotPoints.map((point, index) => {
        const xValue = Number(point[PLOT_X])
        const yValue = Number(point[PLOT_Y])
        const zValue = Number(point[PLOT_Z])
        if (!Number.isFinite(xValue) || !Number.isFinite(yValue)) return null

        const cx = xAxis.scale(xValue)
        const cy = yAxis.scale(yValue)
        if (cx == null || cy == null || !Number.isFinite(cx) || !Number.isFinite(cy)) return null

        const t = Number.isFinite(zValue) ? (zValue - dataMin) / span : 0.5
        const area = rangeMin + t * (rangeMax - rangeMin)
        const radius = Math.sqrt(Math.max(area, 16) / Math.PI)
        const colourValue = String(point.__color ?? '')
        const colour = config[colourValue]?.color ?? '#94a3b8'
        const fill = dotStyle === 'bubble' ? `url(#${toGradientId(colourValue)})` : colour

        return (
          <circle
            key={`bubble-${index}`}
            cx={cx}
            cy={cy}
            r={radius}
            fill={fill}
            stroke={dotStyle === 'bubble' ? colour : undefined}
            strokeWidth={dotStyle === 'bubble' ? 1 : 0}
          />
        )
      })}
    </g>
  )
}

export function ChartScatterContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: ChartScatterUISchema
  dataSchema: ChartScatterDataSchema
  tileFilters?: React.ReactNode
}) {
  const { title = undefined, description = undefined, chartConfig, emphasizeHeader } = uiSchema
  const { xAxis, yAxis, zAxis, config, dotStyle = 'solid', tooltip, renderMode = 'multiSeries', colorKey } = chartConfig
  const { data } = dataSchema
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const { dataKey: xDataKey, type: xType = 'number' } = xAxis
  const { dataKey: yDataKey, type: yType = 'number' } = yAxis
  const { dataKey: zDataKey, name: zName, range: zRange = [1, 2000], showSizeLegend = false } = zAxis

  const useSingleSeries = renderMode === 'singleSeries' && !!colorKey

  if (useSingleSeries) {
    const points: DataPoint[] = isSingleSeriesData(data) ? data : flattenSeriesData(data as SeriesData[])
    const referencedKeys = [
      xDataKey,
      yDataKey,
      zDataKey,
      colorKey,
      ...(tooltip?.fields?.map((field) => field.dataKey) ?? []),
    ]
    const { data: normalizedPoints, mapDataKey } = normalizeRechartsData(points, referencedKeys)
    const normalizedXDataKey = mapDataKey(xDataKey)
    const normalizedYDataKey = mapDataKey(yDataKey)
    const normalizedZDataKey = mapDataKey(zDataKey)
    const normalizedColorKey = mapDataKey(colorKey)
    const normalizedTooltip = tooltip
      ? {
          ...tooltip,
          fields: tooltip.fields?.map((field) => ({
            ...field,
            dataKey: mapDataKey(field.dataKey),
          })),
        }
      : undefined

    const { plotPoints, xCategoryLabels, yCategoryLabels } = buildSingleSeriesPlotPoints({
      points: normalizedPoints,
      xDataKey: normalizedXDataKey,
      yDataKey: normalizedYDataKey,
      zDataKey: normalizedZDataKey,
      colorKey: normalizedColorKey,
      xType,
      yType,
      xDomain: xAxis.domain,
      yDomain: yAxis.domain,
    })

    const colourKeys = Object.keys(config)
    const zValues = plotPoints.map((point) => Number(point[PLOT_Z])).filter((value) => Number.isFinite(value))
    const sizeLegendValues = pickSizeLegendValues(zValues)
    const xLabels = xCategoryLabels ?? []
    const yLabels = yCategoryLabels ?? []
    const xTicks = xLabels.map((_, index) => index)
    const yTicks = yLabels.map((_, index) => index)

    return (
      <div className="flex h-full flex-col">
        {hasHeader && (
          <ComponentHeader emphasize={emphasizeHeader}>
            <ComponentTitle title={title} description={description} />
            <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
          </ComponentHeader>
        )}
        <div className={clsx('flex min-h-0 flex-col', { 'pt-5': hasHeader })} style={{ height: contentHeight }}>
          <ChartContainer
            className="mx-auto aspect-auto h-full min-h-[240px] w-full flex-1 [&_.recharts-wrapper]:overflow-visible"
            config={config}
          >
            <ScatterChart
              data={plotPoints}
              margin={{
                top: 16,
                right: 24,
                bottom: xType === 'category' ? 40 : 20,
                left: yType === 'category' ? 12 : 12,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey={PLOT_X}
                name={xAxis.name}
                label={xAxis.label ? { value: xAxis.label, position: 'insideBottom', offset: -5 } : undefined}
                domain={xLabels.length ? [-0.5, xLabels.length - 0.5] : ['auto', 'auto']}
                ticks={xLabels.length ? xTicks : undefined}
                tickFormatter={xLabels.length ? (value: number) => xLabels[value] ?? '' : undefined}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                type="number"
                dataKey={PLOT_Y}
                name={yAxis.name}
                label={yAxis.label ? { value: yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
                domain={yLabels.length ? [-0.5, yLabels.length - 0.5] : ['auto', 'auto']}
                ticks={yLabels.length ? yTicks : undefined}
                tickFormatter={yLabels.length ? (value: number) => yLabels[value] ?? '' : undefined}
                tick={{ fontSize: 11 }}
                width={yType === 'category' ? 100 : 60}
              />
              <ChartTooltip
                shared={false}
                cursor={{ strokeDasharray: '3 3' }}
                content={
                  <ScatterTooltipContent
                    xAxis={xAxis}
                    yAxis={yAxis}
                    zAxis={zAxis}
                    xDataKey={normalizedXDataKey}
                    yDataKey={normalizedYDataKey}
                    zDataKey={normalizedZDataKey}
                    tooltip={normalizedTooltip}
                  />
                }
              />
              {dotStyle === 'bubble' && <BubbleGradients keys={colourKeys} config={config} />}
              {/* Near-invisible scatter: establishes tooltip targets without drawing bubbles. */}
              <Scatter
                data={plotPoints}
                fill="#000"
                fillOpacity={0}
                shape={<ScatterTooltipTarget radius={getTooltipTargetRadius(zRange)} />}
                legendType="none"
                isAnimationActive={false}
              />
              <Customized
                component={(props: {
                  xAxisMap?: BubbleGridRendererProps['xAxisMap']
                  yAxisMap?: BubbleGridRendererProps['yAxisMap']
                }) => (
                  <BubbleGridRenderer
                    xAxisMap={props.xAxisMap}
                    yAxisMap={props.yAxisMap}
                    plotPoints={plotPoints}
                    config={config}
                    dotStyle={dotStyle}
                    zRange={zRange}
                  />
                )}
              />
            </ScatterChart>
          </ChartContainer>
          <ColourLegend config={config} />
          {showSizeLegend && (
            <div className="mt-2 flex justify-end px-2">
              <SizeLegend title={zName} values={sizeLegendValues} zRange={zRange} />
            </div>
          )}
        </div>
      </div>
    )
  }

  const seriesEntries = (data as SeriesData[]).flatMap((item) => {
    const firstEntry = Object.entries(item)[0]

    return firstEntry ? [{ seriesName: firstEntry[0], seriesData: firstEntry[1] ?? [] }] : []
  })
  const allSeriesData = seriesEntries.flatMap(({ seriesData }) => seriesData)
  const { data: normalizedSeriesData, mapDataKey } = normalizeRechartsData(allSeriesData, [
    xDataKey,
    yDataKey,
    zDataKey,
    ...(tooltip?.fields?.map((field) => field.dataKey) ?? []),
  ])
  const normalizedXDataKey = mapDataKey(xDataKey)
  const normalizedYDataKey = mapDataKey(yDataKey)
  const normalizedZDataKey = mapDataKey(zDataKey)
  const normalizedTooltip = tooltip
    ? {
        ...tooltip,
        fields: tooltip.fields?.map((field) => ({
          ...field,
          dataKey: mapDataKey(field.dataKey),
        })),
      }
    : undefined
  let seriesDataOffset = 0
  const normalizedSeriesEntries = seriesEntries.map(({ seriesName, seriesData }) => {
    const normalizedData = normalizedSeriesData.slice(seriesDataOffset, seriesDataOffset + seriesData.length)
    seriesDataOffset += seriesData.length
    return { seriesName, seriesData: normalizedData }
  })

  // Flatten multi-series into the same plot model used by singleSeries (ComposedChart + Customized).
  // ScatterChart positioning is unreliable in this container; drawing via axis scales is stable.
  const plotPoints = normalizedSeriesEntries.flatMap(({ seriesName, seriesData }) =>
    seriesData.map((point) => ({
      ...point,
      [PLOT_X]: Number(point[normalizedXDataKey]),
      [PLOT_Y]: Number(point[normalizedYDataKey]),
      [PLOT_Z]: Number(point[normalizedZDataKey]),
      __color: seriesName,
    }))
  )

  const colourKeys = Object.keys(config)
  const zValues = plotPoints.map((point) => Number(point[PLOT_Z])).filter((value) => Number.isFinite(value))
  const sizeLegendValues = pickSizeLegendValues(zValues)

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>
        </ComponentHeader>
      )}
      <div className={clsx('flex min-h-0 flex-col', { 'pt-5': hasHeader })} style={{ height: contentHeight }}>
        <ChartContainer
          className="mx-auto aspect-auto h-full min-h-[240px] w-full flex-1 [&_.recharts-wrapper]:overflow-visible"
          config={config}
        >
          <ScatterChart
            data={plotPoints}
            margin={{
              top: 20,
              right: 20,
              bottom: 28,
              left: 12,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey={PLOT_X}
              name={xAxis.name}
              unit={xAxis.unit}
              label={xAxis.label ? { value: xAxis.label, position: 'insideBottom', offset: -5 } : undefined}
              // Match legacy ScatterChart: start at 0 with a nice max (not tight dataMin/dataMax).
              domain={
                xAxis.domain?.every((value): value is number => typeof value === 'number') ? xAxis.domain : [0, 'auto']
              }
            />
            <YAxis
              type="number"
              dataKey={PLOT_Y}
              name={yAxis.name}
              unit={yAxis.unit}
              label={yAxis.label ? { value: yAxis.label, angle: -90, position: 'insideLeft' } : undefined}
              domain={
                yAxis.domain?.every((value): value is number => typeof value === 'number') ? yAxis.domain : [0, 'auto']
              }
              width={60}
            />
            <ChartTooltip
              shared={false}
              cursor={false}
              content={
                <ScatterTooltipContent
                  xAxis={xAxis}
                  yAxis={yAxis}
                  zAxis={zAxis}
                  xDataKey={normalizedXDataKey}
                  yDataKey={normalizedYDataKey}
                  zDataKey={normalizedZDataKey}
                  tooltip={normalizedTooltip}
                />
              }
            />
            {dotStyle === 'bubble' && <BubbleGradients keys={colourKeys} config={config} />}
            <Scatter
              data={plotPoints}
              fill="#000"
              fillOpacity={0}
              shape={<ScatterTooltipTarget radius={getTooltipTargetRadius(zRange)} />}
              legendType="none"
              isAnimationActive={false}
            />
            <Customized
              component={(props: {
                xAxisMap?: BubbleGridRendererProps['xAxisMap']
                yAxisMap?: BubbleGridRendererProps['yAxisMap']
              }) => (
                <BubbleGridRenderer
                  xAxisMap={props.xAxisMap}
                  yAxisMap={props.yAxisMap}
                  plotPoints={plotPoints}
                  config={config}
                  dotStyle={dotStyle}
                  zRange={zRange}
                />
              )}
            />
          </ScatterChart>
        </ChartContainer>
        <ColourLegend config={config} />
        {showSizeLegend && (
          <div className="mt-2 flex justify-end px-2">
            <SizeLegend title={zName} values={sizeLegendValues} zRange={zRange} />
          </div>
        )}
      </div>
    </div>
  )
}

function flattenSeriesData(seriesData: SeriesData[]): DataPoint[] {
  return seriesData.flatMap((item) => {
    const firstEntry = Object.entries(item)[0]
    return firstEntry ? (firstEntry[1] ?? []) : []
  })
}
